<template>
  <iDropdownMenu
    v-if="canPublishStories"
    :active-item="activeItem"
    :items="variants"
    :max-height="500"
    :show-dropdown="dropdownActive"
    :sub-menu-max-height="500"
    :z-index="zIndex"
    overlay-position="bottom-left"
    hover-to-open-sub-menu
    submenu-style="floating"
    @close="toggleDropdown(false)"
    @click="handleSelectPlatform"
  >
    <template #trigger>
      <iButton
        class="add-variant-button"
        size="extraSmall"
        variant="tertiary"
        @click="toggleDropdown"
      >
        <iText
          :style-overrides="{subtleHoverFontColor: 'standard'}"
          variant="subtle"
          :wrap="false"
        >
          {{ variantDisplayCount }}
        </iText>
      </iButton>
    </template>
  </iDropdownMenu>
</template>

<script>
import { mapActions, mapState, mapWritableState } from "pinia";
import { useStoryVariantStore } from "@/stores/story-variant-store";
import { MenuConstants } from "@bloglovin/vue-component-library";
import { useSocialNetworksStore } from "@/stores/social-networks-store";
import { socialNetworkConnectionStatus } from "@/enums/social-network-enums";
import { useActivePublicationFeedStore } from "SmartFeed/stores/feed/active-publication-feed-store";
import { SOCIAL_NETWORK_LINKS } from "&/constants/socials-constants";
import { useStoryStore } from "%/stores/story-store";
import useFanPublicationStore from "%/stores/fan-publication-store";
import { useDraftPublicationFeedStore } from "SmartFeed/stores/feed/draft-publication-feed-store";
import { useCollaboratorStore } from "~/stores/collaborator-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";

export default {
  name: "StoryVariantDropdown",
  props: {
    storyVariantCount: {
      type: Number,
      required: true,
    },
    storyTitle: {
      type: String,
      required: false,
      default: "No title",
    },
    storyId: {
      type: Number,
      required: true,
    },
    parentId: {
      type: Number,
      required: true,
    },
    inPreviewMode: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: Number,
      required: false,
      default: 2,
    },
  },
  emits: ["variant:select"],
  data() {
    return {
      dropdownActive: false,
      activeItem: "",
      parentStory: null,
    };
  },
  computed: {
    ...mapWritableState(useStoryVariantStore, { "feedStoryId": "storyId" }),
    ...mapState(useStoryVariantStore, [
      "variantFeedData",
      "variantNames",
    ]),
    ...mapState(useCollaboratorStore, [
      "canPublishStories",
    ]),
    ...mapState(useStoryStore, [
      "story",
    ]),
    variantDisplayCount() {
      if (this.storyVariantCount < 2) {
        return "Variant";
      }
      return `+${this.storyVariantCount - 1} Variants`;
    },
    variants() {
      const items = [];

      if (!this.canPublishStories) {
        return items;
      }

      items.push({
        name: "new_variant",
        label: "+ New Variant",
        subLabel: "Select a Destination",
        icon: "logo",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: "create_variant",
        subItems: this.allVariants,
      });

      this.variantFeedData.forEach(variant => {
        if (variant.storyId !== this.storyId) {
          items.push({
            name: `variant-${variant.variantId}`,
            label: variant.publishingPlatformName,
            subLabel: this.getVariantTypeName(variant.variantType),
            icon: this.getIcon(variant.variantType),
            actionType: MenuConstants.ACTION_TYPE_LINK_IN_SAME_WINDOW,
            actionValue: `/stories/editor?story_id=${variant.storyId}&preview=${this.inPreviewMode ? "true" : "false"}`,
          });
        }
      });

      if (this.parentStory) {
        items.push({
          name: `variant-${this.parentStory.storyId}`,
          label: this.parentStory.publishingPlatformName,
          subLabel: this.getVariantTypeName(this.parentStory.variantType),
          icon: this.getIcon(this.parentStory.variantType),
          actionType: MenuConstants.ACTION_TYPE_LINK_IN_SAME_WINDOW,
          actionValue: `/stories/editor?story_id=${this.parentStory.storyId}&preview=${this.inPreviewMode ? "true" : "false"}`,
        });
      }

      return items;
    },
    publications() {
      return Object.values(useCreatorPublicationStore().myPublications);
    },
    socialNetworks() {
      return useSocialNetworksStore().socialNetworks.filter(network => [
        socialNetworkConnectionStatus.CONNECTED,
      ].includes(network.status));
    },
    allVariants() {
      const items = [];

      this.publications.forEach(publication => {
        if (
          publication.publication_id === this.story.publishing_platform_id &&
            this.story.publishing_platform_type === "publication" &&
            this.story.variant_type === "publication_post"
        ) {
          return;
        }
        items.push({
          name: publication.name,
          label: publication.name,
          subLabel: "Publication",
          icon: "logo",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: `publication_post.${publication.publication_id}`,
        });
      });

      this.publications.forEach(publication => {
        if (
          publication.publication_id === this.story.publishing_platform_id &&
            this.story.publishing_platform_type === "publication" &&
            this.story.variant_type === "email"
        ) {
          return;
        }
        items.push({
          name: `${publication.name}`,
          label: `${publication.name}`,
          subLabel: "Newsletter",
          icon: "newsletter",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: `email.${publication.publication_id}`,
        });
      });

      items.push({
        name: "syndicationMarketplace",
        label: "Syndication Marketplace",
        subLabel: "Marketplace",
        icon: "shopping-bag",
        actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
        actionValue: `syndication_post.${this.storyId}`,
      });

      this.socialNetworks.forEach(network => {
        const details = this.getSocialNetworkDetails(network.social_network_name);
        if (!details) {
          return;
        }

        items.push({
          name: network.social_network_name,
          label: network.social_network_name,
          subLabel: details.label,
          icon: details.icon,
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: `${details.variant_key}.${network.social_network_account_id}`,
        });
      });

      return items;
    },
  },
  watch: {
    variantFeedData(newData) {
      if (newData) {
        this.updateVariantNames();
      }
    },
  },
  mounted() {
    useActivePublicationFeedStore().getSmartFeed();
    useDraftPublicationFeedStore().getSmartFeed();
  },
  created() {
    this.feedStoryId = this.parentId || this.storyId;
    this.fetchVariants();
    if (this.parentId !== 0) {
      this.fetchStory(this.parentId, story => {
        this.parentStory = story;
      });
    }
  },
  methods: {
    ...mapActions(useStoryVariantStore, [
      "fetchVariants",
      "updateVariantNames",
      "getIcon",
      "getVariantTypeName",
    ]),
    ...mapActions(useStoryStore, [
      "createStoryVariant",
    ]),
    ...mapActions(useFanPublicationStore, ["fetchStory"]),
    getSocialNetworkDetails(name) {
      return SOCIAL_NETWORK_LINKS.find(network => network.name === name);
    },
    handleSelectPlatform(action) {
      const actionType = action.split(".")[0];
      const actionValue = parseInt(action.split(".")[1]);
      if (actionType === "create_variant") {
        this.activeItem = this.activeItem === "new_variant" ? "" : "new_variant";
      } else if (actionType.includes("_post") || actionType === "email") {
        this.createStoryVariant(this.storyId, actionType, actionValue, response => {
          window.location.href = `/stories/editor?story_id=${response.storyId}`;
        });
      }
    },
    toggleDropdown(val = null) {
      this.activeItem = "";
      if (typeof val === "boolean") {
        this.dropdownActive = val;
        return;
      }
      this.dropdownActive = !this.dropdownActive;
    },
  },
};
</script>

<style lang="scss" scoped>
.variant-items-dropdown-container {
  background-color: white;
}

.variant-dropdown-item {
  &:hover {
    background-color: rgb(206, 241, 255);
  }

  cursor: pointer;
}

:deep(.add-variant-button:hover .i-text) {
  color: var(--i-text-color) !important;
}
</style>
